/* Blog-style cards for the "Pages & Blog Post" tab of the Searchanise results page.
 *
 * WHY THIS EXISTS
 * The widget renders that tab as a bare list of titles. Its API can return an
 * image and a description for a page result, but the description is requested
 * with ResultsDescriptionStrings=0 (an app setting we do not control from the
 * theme), and it has no image at all for Shopify /pages/. It also carries no
 * tags, author or date in any form.
 *
 * So the data comes from Shopify instead. Every rendered row already carries the
 * resource id in its own DOM id — <li class="snize-page" id="snize-page-577767440441">
 * — and the href says whether it is an Article or a Page. That is enough to ask
 * the Storefront API for the real record: one nodes() call covering the whole
 * page of results, no second Searchanise request.
 *
 * FAILS SOFT, ALWAYS
 * Every step is guarded. No token, no matching rows, a GraphQL error, a changed
 * class name after an app update — any of them leaves the widget's own list
 * exactly as it was. The grid class is only applied once a row has actually been
 * upgraded, so a half-finished run cannot leave a broken layout on screen.
 */
(function () {
  'use strict';

  var root = document.getElementById('sxc-page-cards');
  if (!root) return;

  var TOKEN = root.dataset.token || '';
  var VERSION = root.dataset.version || '2025-10';
  if (!TOKEN) return;

  /* Theme settings → Search → "Pages & Blog Post tab". Masonry is the default
     and needs no class; grid adds a modifier. The column count only applies
     on desktop, as a custom property the stylesheet reads. */
  var EVEN_GRID = root.dataset.layout === 'grid';
  var COLUMNS = Math.min(4, Math.max(2, parseInt(root.dataset.columns, 10) || 3));

  var LIST_SELECTOR = '.snize-search-results-content';
  var ITEM_SELECTOR = 'li.snize-page';
  /* Two, not the blog feed's three. Several categories here run long enough to
     wrap on their own ("Craft Room Organization & Storage Ideas"), and three of
     those stacked above the headline outweighed it — the chips read as the
     card's subject and the title as a caption. */
  var CATEGORY_TAG_LIMIT = 2;
  var EXCERPT_WORDS = 24;
  /* The 3:2 box the CSS reserves. Set on the <img> as well so the space is held
     even in the moment before the stylesheet applies, which is what stops the
     cards jumping as thumbnails arrive. */
  var IMAGE_W = 600;
  var IMAGE_H = 400;

  /* Keyed by gid. Searchanise re-renders the list on every tab switch and page
     change, and the same articles come back — refetching them would mean a
     request per click. */
  var cache = Object.create(null);

  var QUERY = [
    'query SxcPageCards($ids: [ID!]!) {',
    '  nodes(ids: $ids) {',
    '    __typename',
    '    ... on Article {',
    '      id title tags publishedAt onlineStoreUrl',
    '      authorV2 { name }',
    '      excerpt',
    /* 200 chars comfortably covers the 24 words the card shows after truncation;
       asking for more just pads the response for twenty results at a time. */
    '      content(truncateAt: 200)',
    '      image { url altText }',
    '      blog { handle }',
    '    }',
    '    ... on Page { id title bodySummary onlineStoreUrl }',
    '  }',
    '}'
  ].join('\n');

  /* Shopify's `handle` filter, near enough for building /tagged/ URLs. */
  function handleize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function truncateWords(text, count) {
    var words = String(text || '').replace(/\s+/g, ' ').trim().split(' ');
    if (words.length <= count) return words.join(' ');
    return words.slice(0, count).join(' ') + '...';
  }

  function formatDate(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    /* Matches the blog feed's `| date: '%B %-d, %Y'` — no leading zero on the day. */
    try {
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) {
      return '';
    }
  }

  /* Mirrors snippets/cbl-tags.liquid with resolve_products:false — the blog
     feed's own settings. Drops the author-name tag (the byline already shows it),
     YYYY-MM archive tags (they drive the archive dropdown), and product-handle
     tags (a slug nobody wants to read; the Liquid version can resolve those to a
     product title via all_products, which is not available to us here). */
  function categoryTags(tags, authorName) {
    var authorSlug = handleize(authorName);
    var out = [];

    for (var i = 0; i < (tags || []).length; i++) {
      if (out.length >= CATEGORY_TAG_LIMIT) break;
      var tag = tags[i];
      if (!tag) continue;

      if (handleize(tag) === authorSlug) continue;

      if (/^\d{4}-\d{2}$/.test(tag)) {
        var year = parseInt(tag.slice(0, 4), 10);
        var month = parseInt(tag.slice(5, 7), 10);
        if (year >= 2000 && month >= 1 && month <= 12) continue;
      }

      /* Product-handle shape: one word, all lowercase, contains a hyphen. */
      if (tag.indexOf(' ') === -1 && tag === tag.toLowerCase() && tag.indexOf('-') !== -1) continue;

      out.push(tag);
    }
    return out;
  }

  /* The Storefront `url` is a Shopify CDN address, which resizes from a `width`
     query param — so one field gives us a full srcset without asking for a
     transform per size. */
  function imageMarkup(url, alt) {
    if (!url) return '';
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    var sizes = [300, 400, 600, 800];
    var srcset = sizes
      .map(function (w) { return url + sep + 'width=' + w + ' ' + w + 'w'; })
      .join(', ');

    return (
      '<img class="sxc-card__img" src="' + escapeHtml(url + sep + 'width=' + IMAGE_W) + '"' +
      ' srcset="' + escapeHtml(srcset) + '"' +
      ' sizes="(min-width: 1000px) 30vw, (min-width: 768px) 45vw, 92vw"' +
      ' width="' + IMAGE_W + '" height="' + IMAGE_H + '"' +
      ' alt="' + escapeHtml(alt) + '" loading="lazy" decoding="async">'
    );
  }

  function cardMarkup(node) {
    var isArticle = node.__typename === 'Article';
    var url = node.onlineStoreUrl;
    if (!url) return '';

    var title = node.title || '';
    var blogHandle = (node.blog && node.blog.handle) || '';
    var author = (node.authorV2 && node.authorV2.name) || '';
    var text = isArticle ? (node.excerpt || node.content || '') : (node.bodySummary || '');
    var excerpt = truncateWords(text, EXCERPT_WORDS);

    var tagsHtml = '';
    if (isArticle && blogHandle) {
      var chips = categoryTags(node.tags, author).map(function (tag) {
        return (
          '<a class="sxc-tag" href="/blogs/' + escapeHtml(blogHandle) +
          '/tagged/' + escapeHtml(handleize(tag)) + '">' + escapeHtml(tag) + '</a>'
        );
      });
      if (chips.length) tagsHtml = '<div class="sxc-card__tags">' + chips.join('') + '</div>';
    }

    /* The blog feed bolds the author name in near-black, which is fine on a page
       where the card is the only thing to read. In a grid of search results it
       landed at the same weight as the headline two lines above it, so the eye
       had two competing anchors per card. Here the whole byline is one quiet
       muted row; .sxc-card__author only carries the weight needed to separate
       the name from the "by". */
    var metaHtml = '';
    if (isArticle && (author || node.publishedAt)) {
      var authorHtml = '';
      if (author) {
        var authorName = '<span class="sxc-card__author">' + escapeHtml(author) + '</span>';
        authorHtml = blogHandle
          ? '<span>by <a class="sxc-authorlink" href="/blogs/' + escapeHtml(blogHandle) +
            '/tagged/' + escapeHtml(handleize(author)) + '">' + authorName + '</a></span>'
          : '<span>by ' + authorName + '</span>';
      }
      /* A machine-readable <time> rather than a bare span: the visible text is a
         localised string, so the ISO value is the only unambiguous form. */
      var dateHtml = node.publishedAt
        ? '<time datetime="' + escapeHtml(node.publishedAt) + '">' + escapeHtml(formatDate(node.publishedAt)) + '</time>'
        : '';
      if (authorHtml || dateHtml) metaHtml = '<div class="sxc-card__meta">' + authorHtml + dateHtml + '</div>';
    }

    /* No image at all — every Shopify /pages/ result, which has one nowhere:
       not in the Storefront API, not in Searchanise's index. The blog feed keeps
       an empty 3:2 box in that case because a post without a thumbnail is rare
       and the grid stays aligned. Here it is one row in three, and a column of
       large grey rectangles reads as broken images rather than as a design. So
       the block is dropped and the card starts at its tags. */
    var imgHtml = imageMarkup(node.image && node.image.url, (node.image && node.image.altText) || title);
    var mediaHtml = imgHtml
      ? '<a class="sxc-card__imed" href="' + escapeHtml(url) + '" tabindex="-1" aria-hidden="true">' + imgHtml + '</a>'
      : '';

    /* The thumbnail link is aria-hidden with tabindex="-1" because the title
       below points at the same article — without it every result is announced
       twice. Same reasoning as blocks/blog-feed.liquid. */
    return (
      '<article class="sxc-card' + (mediaHtml ? '' : ' sxc-card--noimage') + '">' +
      mediaHtml +
      '<div class="sxc-card__body">' +
      tagsHtml +
      '<h3 class="sxc-card__title"><a href="' + escapeHtml(url) + '">' + escapeHtml(title) + '</a></h3>' +
      (excerpt ? '<p class="sxc-card__excerpt">' + escapeHtml(excerpt) + '</p>' : '') +
      metaHtml +
      '</div>' +
      '</article>'
    );
  }

  function gidFor(item) {
    var match = /snize-page-(\d+)/.exec(item.id || '');
    if (!match) return null;
    var link = item.querySelector('a[href]');
    var href = link ? link.getAttribute('href') || '' : '';
    /* /blogs/<blog>/<article> is an Article; everything else on this tab is a Page. */
    var type = href.indexOf('/blogs/') === 0 ? 'Article' : 'Page';
    return 'gid://shopify/' + type + '/' + match[1];
  }

  function fetchNodes(ids) {
    return fetch('/api/' + VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': TOKEN
      },
      body: JSON.stringify({ query: QUERY, variables: { ids: ids } })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Storefront API responded with ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        if (payload.errors && payload.errors.length) throw new Error(payload.errors[0].message);
        return (payload.data && payload.data.nodes) || [];
      });
  }

  function paint(items) {
    var painted = 0;
    items.forEach(function (item) {
      var gid = item.__sxcGid;
      var node = gid && cache[gid];
      if (!node) return;
      var html = cardMarkup(node);
      if (!html) return;
      item.innerHTML = html;
      item.classList.add('sxc-done');
      painted++;
    });

    if (painted) {
      var list = items[0].closest(LIST_SELECTOR);
      if (list) {
        list.classList.add('sxc-grid');
        list.classList.toggle('sxc-grid--even', EVEN_GRID);
        list.style.setProperty('--sxc-columns', COLUMNS);
      }
    }
  }

  var running = false;

  /* The widget reuses the same <ul> for every tab, so the grid class outlives a
     switch to Products or Collections and would lay their rows out as cards.
     Runs before the early return below, because the Products tab has no
     .snize-page rows at all and would otherwise never reach this. */
  function dropStaleGrid(container) {
    var lists = container.querySelectorAll(LIST_SELECTOR + '.sxc-grid');
    for (var i = 0; i < lists.length; i++) {
      if (!lists[i].querySelector(ITEM_SELECTOR)) {
        lists[i].classList.remove('sxc-grid', 'sxc-grid--even');
        lists[i].style.removeProperty('--sxc-columns');
      }
    }
  }

  function upgrade() {
    if (running) return;

    var container = document.getElementById('snize_results');
    if (!container) return;

    dropStaleGrid(container);

    var items = Array.prototype.slice
      .call(container.querySelectorAll(ITEM_SELECTOR))
      .filter(function (item) { return !item.classList.contains('sxc-done'); });
    if (!items.length) return;

    var wanted = [];
    items.forEach(function (item) {
      var gid = gidFor(item);
      item.__sxcGid = gid;
      if (gid && !cache[gid] && wanted.indexOf(gid) === -1) wanted.push(gid);
    });

    if (!wanted.length) {
      paint(items);
      return;
    }

    running = true;
    fetchNodes(wanted)
      .then(function (nodes) {
        nodes.forEach(function (node) {
          /* A deleted or unpublished record comes back as null — skip it and the
             row keeps the widget's plain title. */
          if (node && node.id) cache[node.id] = node;
        });
        paint(items);
      })
      .catch(function () {
        /* Deliberately silent: the plain list is a perfectly good fallback and a
           console error on every search would drown the theme-dev monitor. */
      })
      .then(function () {
        running = false;
      });
  }

  /* ------------------------------------------------------------------------
     NEWEST FIRST
     "Could we sort the Pages & Blog Posts with the latest ones shown first?"
     (Jossa's review, 09/18, item 6.) Searchanise ranks this tab by match only
     and sends no dates, so it cannot be asked for that order, and sorting the
     20 rows it returns would still leave newer posts on the pages after them.

     So in Newest mode the tab is ours. Shopify's own article search takes the
     same words and returns the matches already sorted by publish date, a page
     at a time; the few matching /pages/ (90 for "stamp") come in one call and
     are merged in by the date they were created — their "updated" date was
     bulk-changed on 08/13/2026 and says nothing. The cards are the same
     cardMarkup() as above, in the same list markup, so the card styles apply
     unchanged (the list is always the even grid; see renderNewest).

     A "Sort by" menu sits where Sort By sits on the other tabs: Newest (the
     default) or Best match, which is simply the widget's own list, untouched.
     The choice rides in the URL as ?pages_sort=match, so Back and a shared
     link keep it. Anything failing drops back to Best match.
     ------------------------------------------------------------------------ */

  var SORT_PARAM = 'pages_sort';
  var SORT_MATCH = 'match';
  var NEWEST_CLASS = 'sxc-newest'; // on <html>; the widget rewrites #snize_results' classes
  var PAGE_SIZE = 20; // the widget's own page size on this tab
  var ARTICLE_FIELDS =
    '__typename id title tags publishedAt onlineStoreUrl authorV2 { name } excerpt ' +
    'content(truncateAt: 200) image { url altText } blog { handle }';

  /* Storefront search syntax treats quotes, parentheses, colons and the
     boolean words as operators; a shopper typing them should get a plain
     search. Same rule as the Classes tab. */
  function searchTerm() {
    return (new URLSearchParams(window.location.search).get('q') || '')
      .replace(/[()":\\*]/g, ' ')
      .replace(/\b(AND|OR|NOT)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 100);
  }

  var newest = {
    term: searchTerm(),
    articles: [], // fetched, newest first
    cursor: null,
    more: true, // more articles to fetch
    pages: null, // every matching page, newest first; null until fetched
    shown: PAGE_SIZE,
    loading: false,
    failed: false,
  };

  function storefront(query, variables) {
    return fetch('/api/' + VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': TOKEN
      },
      body: JSON.stringify({ query: query, variables: variables })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Storefront API responded with ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        if (payload.errors && payload.errors.length) throw new Error(payload.errors[0].message);
        return payload.data;
      });
  }

  function fetchPages() {
    return storefront(
      'query SxcNewestPages($q: String!) { pages(query: $q, first: 250) { nodes { __typename id title bodySummary onlineStoreUrl createdAt } } }',
      { q: newest.term }
    ).then(function (data) {
      newest.pages = data.pages.nodes
        .filter(function (n) { return n.onlineStoreUrl; })
        .sort(function (a, b) { return b.createdAt < a.createdAt ? -1 : 1; });
    });
  }

  function fetchArticles() {
    return storefront(
      'query SxcNewestArticles($q: String!, $after: String) { articles(query: $q, first: ' + PAGE_SIZE +
        ', after: $after, sortKey: PUBLISHED_AT, reverse: true) { nodes { ' + ARTICLE_FIELDS +
        ' } pageInfo { hasNextPage endCursor } } }',
      { q: newest.term, after: newest.cursor }
    ).then(function (data) {
      var a = data.articles;
      newest.articles = newest.articles.concat(a.nodes.filter(function (n) { return n.onlineStoreUrl; }));
      newest.cursor = a.pageInfo.endCursor;
      newest.more = a.pageInfo.hasNextPage;
    });
  }

  function dateOf(node) {
    return node.publishedAt || node.createdAt || '';
  }

  /* The first `count` results in date order. A page can only be placed once
     every article newer than it has been fetched, which holds while the last
     fetched article is older than it, or once there are no more articles. */
  function merged(count) {
    var out = [];
    var a = 0;
    var p = 0;
    var pages = newest.pages || [];
    var arts = newest.articles;
    while (out.length < count) {
      var art = arts[a];
      var page = pages[p];
      if (!art && !page) break;
      if (!art && newest.more) break; // need more articles before placing anything else
      if (page && (!art || dateOf(page) > dateOf(art))) {
        out.push(page);
        p++;
      } else {
        out.push(art);
        a++;
      }
    }
    return out;
  }

  function total() {
    return (newest.pages ? newest.pages.length : 0) + newest.articles.length;
  }

  /* Fetch articles until the first `shown` results can be placed. */
  function ensureLoaded() {
    if (newest.loading || newest.failed) return;
    var need = newest.pages === null || (newest.more && merged(newest.shown).length < newest.shown);
    if (!need) return;

    newest.loading = true;
    renderNewest();
    var steps = [];
    if (newest.pages === null) steps.push(fetchPages());
    if (newest.more) steps.push(fetchArticles());
    Promise.all(steps)
      .then(function () {
        newest.loading = false;
        ensureLoaded(); // a run of old pages can need a second batch of articles
        renderNewest();
      })
      .catch(function () {
        /* Silent, like the cards above: Best match is a perfectly good
           fallback, and the menu stays so the shopper can see which it is. */
        newest.loading = false;
        newest.failed = true;
        syncNewest();
      });
  }

  function sortChoice() {
    return new URLSearchParams(window.location.search).get(SORT_PARAM) === SORT_MATCH ? SORT_MATCH : 'newest';
  }

  function setSortChoice(value) {
    var params = new URLSearchParams(window.location.search);
    if (value === SORT_MATCH) params.set(SORT_PARAM, SORT_MATCH);
    else params.delete(SORT_PARAM);
    var qs = params.toString();
    window.history.pushState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    syncNewest();
  }

  function onPagesTab(container) {
    return container.classList.contains('snize-tab-selected-pages') &&
      !document.documentElement.classList.contains('sxr-view-classes');
  }

  function ensureMenu(container) {
    var panel = container.querySelector('.snize-main-panel');
    if (!panel) return null;
    var wrap = panel.querySelector('.sxc-sort');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'sxc-sort';
      wrap.innerHTML =
        '<label class="sxc-sort__label" for="sxc-sort-select">Sort by</label>' +
        '<select id="sxc-sort-select" class="sxc-sort__select">' +
        '<option value="newest">Newest</option>' +
        '<option value="' + SORT_MATCH + '">Best match</option>' +
        '</select>';
      wrap.querySelector('select').addEventListener('change', function (event) {
        setSortChoice(event.target.value);
      });
      panel.appendChild(wrap);
    }
    var select = wrap.querySelector('select');
    var value = newest.failed ? SORT_MATCH : sortChoice();
    if (select.value !== value) select.value = value;
    return wrap;
  }

  function ensurePanel(container) {
    var panel = document.getElementById('sxc-newest-panel');
    if (!panel || panel.parentElement !== container) {
      if (panel) panel.remove();
      panel = document.createElement('div');
      panel.id = 'sxc-newest-panel';
      var content = container.querySelector('.snize-search-results-main-content');
      if (content) content.after(panel);
      else container.appendChild(panel);
    }
    return panel;
  }

  function renderNewest() {
    var container = document.getElementById('snize_results');
    if (!container) return;
    var panel = ensurePanel(container);
    var items = merged(newest.shown);
    var html;

    if (!items.length && newest.loading) {
      html = '<p class="sxc-newest__status" role="status">Loading the newest posts&hellip;</p>';
    } else if (!items.length) {
      html = '<p class="sxc-newest__status">No pages or blog posts found.</p>';
    } else {
      var cards = items.map(function (node) {
        var card = cardMarkup(node);
        return card ? '<li class="snize-page sxc-done">' + card + '</li>' : '';
      }).join('');
      var hasMore = newest.more || total() > items.length;
      /* Always the even grid here, whatever the layout setting. Masonry fills
         each column top to bottom, so its first row showed the 1st, 8th and
         15th newest: a date order has to read across. */
      html =
        '<ul class="snize-search-results-content sxc-grid sxc-grid--even' +
          '" style="--sxc-columns: ' + COLUMNS + '">' + cards + '</ul>' +
        (hasMore
          ? '<div class="sxc-newest__more"><button type="button" class="sxc-newest__more-button"' +
            (newest.loading ? ' disabled' : '') + '>' + (newest.loading ? 'Loading&hellip;' : 'Show more') + '</button></div>'
          : '');
    }

    if (panel.dataset.rendered !== html) {
      panel.dataset.rendered = html;
      panel.innerHTML = html;
    }
  }

  /* Idempotent, and it only writes when something differs, so running on
     every widget redraw settles instead of looping. */
  function syncNewest() {
    var container = document.getElementById('snize_results');
    if (!container || !newest.term) return;

    var pagesTab = onPagesTab(container);
    if (pagesTab) ensureMenu(container);

    var on = pagesTab && !newest.failed && sortChoice() !== SORT_MATCH;
    document.documentElement.classList.toggle(NEWEST_CLASS, on);
    if (!on) return;

    ensureLoaded();
    renderNewest();
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('.sxc-newest__more-button');
    if (!button) return;
    newest.shown += PAGE_SIZE;
    ensureLoaded();
    renderNewest();
  });

  window.addEventListener('popstate', syncNewest);

  function refresh() {
    upgrade();
    syncNewest();
  }

  /* The widget's own event, which its config already uses elsewhere. It fires
     after every render — first paint, tab switch, pagination, filter change.
     Searchanise.$ only exists once the app has booted, so binding is retried:
     this script is deferred, but the app loads on its own schedule and may not
     have run yet. */
  var bound = false;

  function bindWidgetEvent() {
    if (bound) return true;
    var $ = window.Searchanise && window.Searchanise.$;
    if (!$) return false;
    $(document).on('Searchanise.ResultsUpdated', function () { setTimeout(refresh, 0); });
    bound = true;
    return true;
  }

  bindWidgetEvent();
  document.addEventListener('Searchanise.Loaded', bindWidgetEvent);

  /* Belt and braces, and the only trigger that can be relied on for the first
     render: the app INJECTS #snize_results itself, so that element does not
     exist when this script runs and cannot be observed directly. Watching body
     catches the injection, every later re-render, and any app change that stops
     the event above firing. upgrade() early-returns once every row is marked
     sxc-done, so the extra calls cost a querySelectorAll and nothing more. */
  if (window.MutationObserver) {
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      setTimeout(function () {
        scheduled = false;
        bindWidgetEvent();
        refresh();
      }, 120);
    }).observe(document.body, { childList: true, subtree: true });
  }

  refresh();
})();
